import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { settlementActions } from "src/features/fields/context/settlement-slice";
import { getAllCompanyDetails } from "src/services/company-api";
import { queryDivisionDetails } from "src/services/division-api";
import { getStockistDetails } from "src/services/reports-api";
import { queryProductDetails } from "src/services/product-api";

type TabType = "EXPECTED" | "RELEASED" | "SETTLED" | "SEARCH";

const TAB_CONFIG = {
  EXPECTED: {
    divisionKey: "expectedDivision",
    stockistKey: "expectedStockist",
    productKey: "expectedProduct",
  },
  RELEASED: {
    divisionKey: "releasedDivision",
    stockistKey: "releasedStockist",
    productKey: "releasedProduct",
  },
  SETTLED: {
    divisionKey: "settledDivision",
    stockistKey: "settledStockist",
    productKey: "settledProduct",
  },
  SEARCH: {
    stockistKey: "searchStockist",
  },
};

const getSelected = (arr?: any[]) =>
  Array.isArray(arr) && arr.length ? arr[0] : null;

export const useSettlementFilters = (filtersByTab: any) => {
  const dispatch = useDispatch();
  const companyLoaded = useRef(false);

  const prevCompany = useRef<any>({});
  const prevDivision = useRef<any>({});

  useEffect(() => {
    if (companyLoaded.current) return;

    companyLoaded.current = true;

    getAllCompanyDetails()
      .then((res: any) => {
        dispatch(
          settlementActions.setSettlementFiltersData({
            key: "company",
            value: res.data,
          })
        );
      })
      .catch(console.error);
  }, [dispatch]);

  useEffect(() => {
    (["EXPECTED", "RELEASED", "SETTLED", "SEARCH"] as TabType[]).forEach(
      async (tab) => {
        const filters = filtersByTab[tab];
        const config: any = TAB_CONFIG[tab];

        const company = getSelected(filters?.company);
        const division = getSelected(filters?.division);

        const prevCompanyCode = prevCompany.current[tab];
        const prevDivisionCode = prevDivision.current[tab];

        if (!company && prevCompanyCode) {
          prevCompany.current[tab] = null;
          prevDivision.current[tab] = null;

          dispatch(
            settlementActions.setFilter({
              tab,
              key: "division",
              value: undefined,
            })
          );

          dispatch(
            settlementActions.setFilter({
              tab,
              key: "stockist",
              value: undefined,
            })
          );

          dispatch(
            settlementActions.setFilter({
              tab,
              key: "product",
              value: undefined,
            })
          );

          dispatch(
            settlementActions.setSettlementFiltersData({
              key: config.divisionKey,
              value: [],
            })
          );

          dispatch(
            settlementActions.setSettlementFiltersData({
              key: config.stockistKey,
              value: [],
            })
          );

          dispatch(
            settlementActions.setSettlementFiltersData({
              key: config.productKey,
              value: [],
            })
          );

          return;
        }

        if (!division && prevDivisionCode) {
          prevDivision.current[tab] = null;

          dispatch(
            settlementActions.setFilter({
              tab,
              key: "product",
              value: undefined,
            })
          );

          dispatch(
            settlementActions.setSettlementFiltersData({
              key: config.productKey,
              value: [],
            })
          );
        }

        if (company && prevCompanyCode !== company.code) {
          prevCompany.current[tab] = company.code;

          try {
            const [divisionRes, stockistRes] = await Promise.all([
              queryDivisionDetails(
                `/details/?fields=division_name&company_code=${company.code}`
              ),
              getStockistDetails({
                company_code: company.company_code || company.code,
              }),
            ]);

            dispatch(
              settlementActions.setSettlementFiltersData({
                key: config.divisionKey,
                value: divisionRes.data,
              })
            );

            dispatch(
              settlementActions.setSettlementFiltersData({
                key: config.stockistKey,
                value:
                  stockistRes?.data?.data?.data ||
                  stockistRes?.data ||
                  [],
              })
            );
          } catch (err) {
            console.error(err);
          }
        }

        if (
          company &&
          division &&
          prevDivisionCode !== division.company_divisioncode
        ) {
          prevDivision.current[tab] =
            division.company_divisioncode;

          try {
            const res = await queryProductDetails(
              `/details?company_id=${company.code}&division_id=${division.company_divisioncode}&company_divisioncode_visible=true&selectcompany_getactivedivisons=true`
            );

            dispatch(
              settlementActions.setSettlementFiltersData({
                key: config.productKey,
                value: res?.data?.data || res?.data,
              })
            );
          } catch (err) {
            console.error(err);
          }
        }
      }
    );
  }, [filtersByTab, dispatch]);
};
